import axios from 'axios';
import { 
  Card, 
  Deck,
  DeckCreate,
  DeckUpdate,
  Tournament, 
  TournamentCreate,
  Circuit, 
  CircuitCreate,
  Ruleset,
  RulesetCreate,
  DeckSubmission,
  DeckSubmissionCreate,
  DeckSubmissionUpdate,
  DeckSubmissionReview,
  PriceSnapshot,
  TournamentFollow,
  TournamentOrganizer,
  TournamentOrganizerCreate,
  TournamentRegistration,
  TournamentRegistrationCreate,
  TournamentRegistrationStatus,
  DeckPreviewRequest,
  DeckPreviewResponse,
  DeckImportRequest,
  DeckImportResponse,
  DeckSubmitToTournamentRequest,
  BannedCard,
  BannedCardCreate,
  BanlistResponse,
  Format,
  FormatsResponse,
  UserListResponse,
  UserListPaginatedResponse,
  UserStatisticsResponse,
  SystemStatistics,
} from '../types';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8000'
    : '/api');

// Debug log to help troubleshoot API URL issues (remove in production if needed)
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
  console.log('[API Config] API_BASE_URL:', API_BASE_URL);
  console.log('[API Config] REACT_APP_API_URL:', process.env.REACT_APP_API_URL || '(not set)');
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Debug logging for request URL
    if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
      console.log('[API Request]', config.method?.toUpperCase(), config.url);
      const fullURL = (config.baseURL || '') + (config.url || '');
      console.log('[API Request] Full URL:', fullURL);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Debug logging for errors
    if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
      const errorConfig = error.config;
      const fullURL = errorConfig ? ((errorConfig.baseURL || '') + (errorConfig.url || '')) : 'unknown';
      console.error('[API Error]', {
        message: error.message,
        code: error.code,
        url: errorConfig?.url,
        baseURL: errorConfig?.baseURL,
        fullURL: fullURL,
        status: error.response?.status,
        statusText: error.response?.statusText,
      });
    }
    if (error.response?.status === 401) {
      // Token expired or invalid, clear it
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Cards API
export const cardsApi = {
  getAll: () => api.get<Card[]>('/cards'),
  getById: (id: string) => api.get<Card>(`/cards/${id}`),
  search: (query: string, offset?: number, tournamentId?: string) => {
    const params = new URLSearchParams();
    if (query) params.append('name', query);
    if (offset) params.append('offset', offset.toString());
    if (tournamentId) params.append('tournament_id', tournamentId);
    return api.get<Card[]>(`/cards/search?${params.toString()}`);
  },
  getTournamentPrices: (tournamentId: string, cardIds: string[]) => {
    const params = new URLSearchParams();
    params.append('tournament_id', tournamentId);
    if (cardIds.length > 0) {
      params.append('card_ids', cardIds.join(','));
    }
    return api.get(`/cards/tournament-prices?${params.toString()}`);
  },
};

// Decks API (My Decks)
export const decksApi = {
  getAll: () => api.get<Deck[]>('/decks'),
  getById: (id: string) => api.get<Deck>(`/decks/${id}`),
  create: (deck: DeckCreate) => api.post<Deck>('/decks', deck),
  update: (id: string, deck: DeckUpdate) => api.put<Deck>(`/decks/${id}`, deck),
  delete: (id: string) => api.delete(`/decks/${id}`),
  copy: (id: string) => api.post<Deck>(`/decks/${id}/copy`),
  preview: (id: string, request: DeckPreviewRequest) => api.post<DeckPreviewResponse>(`/decks/${id}/preview`, request),
  submitToTournament: (id: string, request: DeckSubmitToTournamentRequest) => api.post<DeckSubmission>(`/decks/${id}/submit`, request),
  importFromUrl: (request: DeckImportRequest) => api.post<DeckImportResponse>('/decks/import', request),
  syncFromSource: (id: string) => api.post<Deck>(`/decks/${id}/sync`),
};

// Circuits API
export const circuitsApi = {
  getAll: () => api.get<Circuit[]>('/tournaments/circuits'),
  getById: (id: string) => api.get<Circuit>(`/tournaments/circuits/${id}`),
  create: (circuit: CircuitCreate) => api.post<Circuit>('/tournaments/circuits', circuit),
  update: (id: string, circuit: Partial<Circuit>) => api.put<Circuit>(`/tournaments/circuits/${id}`, circuit),
  delete: (id: string) => api.delete(`/tournaments/circuits/${id}`),
};

// Rulesets API
export const rulesetsApi = {
  getAll: () => api.get<Ruleset[]>('/tournaments/rulesets'),
  getById: (id: string) => api.get<Ruleset>(`/tournaments/rulesets/${id}`),
  create: (ruleset: RulesetCreate) => api.post<Ruleset>('/tournaments/rulesets', ruleset),
  update: (id: string, ruleset: Partial<Ruleset>) => api.put<Ruleset>(`/tournaments/rulesets/${id}`, ruleset),
  delete: (id: string) => api.delete(`/tournaments/rulesets/${id}`),
};

// Banlists API
export const banlistsApi = {
  getAll: (rulesetId: string) => api.get<BannedCard[]>(`/rulesets/${rulesetId}/banlist`),
  addCard: (rulesetId: string, card: BannedCardCreate) => 
    api.post<BannedCard>(`/rulesets/${rulesetId}/banlist/cards`, card),
  removeCard: (rulesetId: string, bannedCardId: string) => 
    api.delete(`/rulesets/${rulesetId}/banlist/cards/${bannedCardId}`),
};

// Formats API
export const formatsApi = {
  getAll: () => api.get<FormatsResponse>('/tournaments/formats'),
};

// Tournaments API
export const tournamentsApi = {
  getAll: (circuitId?: string) => api.get<Tournament[]>(`/tournaments${circuitId ? `?circuit_id=${circuitId}` : ''}`),
  getById: (id: string) => api.get<Tournament>(`/tournaments/${id}`),
  create: (tournament: TournamentCreate) => api.post<Tournament>('/tournaments', tournament),
  update: (id: string, tournament: Partial<Tournament>) => api.put<Tournament>(`/tournaments/${id}`, tournament),
  delete: (id: string) => api.delete(`/tournaments/${id}`),
  follow: (tournamentId: string) => api.post<TournamentFollow>(`/tournaments/${tournamentId}/follow`),
  unfollow: (tournamentId: string) => api.delete(`/tournaments/${tournamentId}/follow`),
  getFollowed: () => api.get<Tournament[]>('/tournaments/followed'),
  getWithSubmissions: () => api.get<Tournament[]>('/tournaments/with-submissions'),
  // TO submission management
  getSubmissions: (tournamentId: string, status?: string) => 
    api.get<DeckSubmission[]>(`/tournaments/${tournamentId}/submissions${status ? `?status=${status}` : ''}`),
  reviewSubmission: (tournamentId: string, submissionId: string, review: DeckSubmissionReview) => 
    api.put<DeckSubmission>(`/tournaments/${tournamentId}/submissions/${submissionId}/review`, review),
  // TO management
  getOrganizers: (tournamentId: string) => api.get<TournamentOrganizer[]>(`/tournaments/${tournamentId}/organizers`),
  addOrganizer: (tournamentId: string, organizer: TournamentOrganizerCreate) => 
    api.post(`/tournaments/${tournamentId}/organizers`, organizer),
  removeOrganizer: (tournamentId: string, userId: number) => 
    api.delete(`/tournaments/${tournamentId}/organizers/${userId}`),
  // Registration
  register: (tournamentId: string) => api.post<TournamentRegistration>(`/tournaments/${tournamentId}/register`),
  getRegistration: (tournamentId: string) => api.get<TournamentRegistration>(`/tournaments/${tournamentId}/registration`),
  getRegistrations: () => api.get<TournamentRegistration[]>('/tournaments/registrations'),
  getTournamentRegistrations: (tournamentId: string) => 
    api.get<TournamentRegistration[]>(`/tournaments/${tournamentId}/registrations`),
  acceptRegistration: (tournamentId: string, userId: number) => 
    api.put<TournamentRegistration>(`/tournaments/${tournamentId}/registrations/${userId}/accept`),
  rejectRegistration: (tournamentId: string, userId: number) => 
    api.put<TournamentRegistration>(`/tournaments/${tournamentId}/registrations/${userId}/reject`),
};

// Deck Submissions API
export const submissionsApi = {
  getAll: () => api.get<DeckSubmission[]>('/decks/submissions'),
  getById: (id: string, viewAs?: string) => api.get<DeckSubmission>(`/decks/submissions/${id}${viewAs ? `?view_as=${viewAs}` : ''}`),
  getBreakdown: (id: string) => api.get(`/decks/submissions/${id}/breakdown`),
  create: (submission: DeckSubmissionCreate) => api.post<DeckSubmission>('/decks/submissions', submission),
  update: (id: string, submission: DeckSubmissionUpdate) => api.put<DeckSubmission>(`/decks/submissions/${id}`, submission),
  delete: (id: string) => api.delete(`/decks/submissions/${id}`),
  submit: (id: string) => api.post<DeckSubmission>(`/decks/submissions/${id}/submit`),
};

// Admin API (for TOs and Admins)
export const adminApi = {
  // TO endpoints
  getTournamentSubmissions: (tournamentId: string, status?: string) => 
    api.get<DeckSubmission[]>(`/admin/submissions/${tournamentId}${status ? `?status=${status}` : ''}`),
  reviewSubmission: (submissionId: string, review: DeckSubmissionReview) => 
    api.put<DeckSubmission>(`/admin/submissions/${submissionId}/review`, review),
  // Admin-only user management endpoints
  getAllUsers: (params?: {
    skip?: number;
    limit?: number;
    search?: string;
    role?: string;
    is_active?: boolean;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    const queryString = queryParams.toString();
    return api.get<UserListPaginatedResponse>(`/admin/users${queryString ? `?${queryString}` : ''}`);
  },
  getUserById: (userId: number) => 
    api.get<UserListResponse>(`/admin/users/${userId}`),
  updateUserRole: (userId: number, role: 'PLAYER' | 'TO' | 'ADMIN') => 
    api.put<UserListResponse>(`/admin/users/${userId}/role?role=${role}`),
  updateUserStatus: (userId: number, isActive: boolean) => 
    api.put<UserListResponse>(`/admin/users/${userId}/status?is_active=${isActive}`),
  getUserStatistics: (userId: number) => 
    api.get<UserStatisticsResponse>(`/admin/users/${userId}/statistics`),
  getSystemStatistics: () => 
    api.get<SystemStatistics>('/admin/statistics'),
};

// Price Snapshots API
export const snapshotsApi = {
  getAll: () => api.get<PriceSnapshot[]>('/snapshots'),
  getById: (id: string) => api.get<PriceSnapshot>(`/snapshots/${id}`),
  create: (formData: FormData) => api.post<PriceSnapshot>('/snapshots', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  delete: (id: string) => api.delete(`/snapshots/${id}`),
  getScryfallLatestInfo: () => api.get('/snapshots/scryfall/latest-info'),
  autoDownloadScryfall: () => api.post('/snapshots/scryfall/auto-download'),
};

export default api;
